/**
 * Sprint 41 — Helpers puros para calcular rangos de presets de propinas.
 *
 * IMPORTANTE: trabajan SIEMPRE en hora local del usuario y devuelven ISO
 * strings (UTC) listas para el backend. Es lo que evita el bug que el
 * usuario reportó: "pongo HOY-HOY y me da CERO".
 *
 * - "HOY"           → 00:00:00 hoy local → ahora (now)
 * - "ESTA SEMANA"   → lunes 00:00:00 local → ahora
 * - "ÚLTIMOS N DÍAS"→ (hoy - (N-1) días) 00:00:00 local → ahora
 *
 * El extremo superior es `now()` y no `23:59:59` para que pagos futuros
 * (raro pero posible si el reloj del POS está adelantado vs el reloj del
 * banco) no entren en el rango.
 */

export type PresetRango = 'hoy' | 'semana' | 'ultimos_n_dias' | 'personalizado';

export interface RangoIso {
  fechaInicio: string; // ISO UTC
  fechaFin: string;    // ISO UTC
}

/**
 * Inicio de día local en UTC ISO.
 * `new Date(year, month, day)` ya devuelve medianoche local — el .toISOString()
 * lo convierte a UTC para el backend.
 */
function inicioDiaLocal(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

export function rangoHoy(ahora: Date = new Date()): RangoIso {
  return {
    fechaInicio: inicioDiaLocal(ahora).toISOString(),
    fechaFin: ahora.toISOString(),
  };
}

/**
 * Semana: lunes como día 1 (convención latam/europea, no domingo USA).
 * Si hoy es lunes, devuelve [lun 00:00, ahora].
 */
export function rangoSemana(ahora: Date = new Date()): RangoIso {
  const hoy0 = inicioDiaLocal(ahora);
  const diaSemana = hoy0.getDay(); // 0=dom, 1=lun, ..., 6=sab
  // diasDesdeLunes: 0 si lunes, 1 si martes, ..., 6 si domingo.
  const diasDesdeLunes = (diaSemana + 6) % 7;
  const lunes = new Date(hoy0);
  lunes.setDate(hoy0.getDate() - diasDesdeLunes);
  return {
    fechaInicio: lunes.toISOString(),
    fechaFin: ahora.toISOString(),
  };
}

/**
 * Últimos N días contando HOY como día 1.
 * N=1 = solo hoy. N=7 = los 7 días terminados HOY (incluyente).
 * Devuelve `null` si N inválido (≤0).
 */
export function rangoUltimosNDias(n: number, ahora: Date = new Date()): RangoIso | null {
  if (!Number.isFinite(n) || n <= 0) return null;
  const nEntero = Math.floor(n);
  const hoy0 = inicioDiaLocal(ahora);
  const desde = new Date(hoy0);
  desde.setDate(hoy0.getDate() - (nEntero - 1));
  return {
    fechaInicio: desde.toISOString(),
    fechaFin: ahora.toISOString(),
  };
}

/**
 * Convierte dos `<input type="date">` (YYYY-MM-DD en hora local del navegador)
 * a un rango ISO UTC con extremos 00:00 — 23:59:59 locales. Para "personalizado".
 * Sigue la convención que usaba el form viejo, pero centralizada.
 */
export function rangoPersonalizado(yyyymmddInicio: string, yyyymmddFin: string): RangoIso {
  const [y1, m1, d1] = yyyymmddInicio.split('-').map(Number);
  const [y2, m2, d2] = yyyymmddFin.split('-').map(Number);
  const inicio = new Date(y1, m1 - 1, d1, 0, 0, 0, 0);
  const fin = new Date(y2, m2 - 1, d2, 23, 59, 59, 999);
  return {
    fechaInicio: inicio.toISOString(),
    fechaFin: fin.toISOString(),
  };
}

/**
 * Formatea un ISO UTC a YYYY-MM-DD local (para precargar inputs).
 */
export function isoToYYYYMMDD(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}
